import React, { FC, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";

import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { LoadingSpinner } from "@mapmycustomers/ui";

import FilePreviewPage from "@app/component/FilePreviewPage";
import EmbeddedModal from "@app/component/modal/EmbeddedModal";
import Path from "@app/enum/Path";

import styles from "./Resources.module.scss";
import { fetchResource } from "./store/actions";

type FilesPageProps = typeof mapDispatchToProps;

const FilePage: FC<FilesPageProps> = ({ onFetchResource }) => {
  const intl = useIntl();
  const history = useHistory();
  const { folderId, resourceId } = useParams<{ folderId?: string; resourceId?: string }>();

  const [resource, setResource] = useState<Resource>();
  const [loading, setLoading] = useState(false);
  const [isNoAccess, setNoAccess] = useState(false);

  const handleClose = useCallback(() => {
    history.push(
      `${Path.RESOURCES}${resource?.trashedAt ? "/trash" : folderId ? `/folders/${folderId}` : ""}`
    );
  }, [folderId, history, resource?.trashedAt]);

  useEffect(() => {
    if (resourceId) {
      setLoading(true);
      onFetchResource({
        id: parseInt(resourceId),
        failureCallback: (error) => {
          if (error.status === 403) {
            setNoAccess(true);
          }
          setLoading(false);
        },
        successCallback: (resource) => {
          if (resource.url) {
            history.push(Path.RESOURCES);
          } else {
            setResource(resource);
          }
          setLoading(false);
        },
      });
    }
  }, [history, onFetchResource, resourceId]);

  return (
    <EmbeddedModal open={!!resourceId}>
      {loading ? (
        <LoadingSpinner global />
      ) : !resource || !resource.file ? (
        <div className={styles.errorContainer}>
          <FontAwesomeIcon className={styles.alertIcon} icon={faTriangleExclamation} />
          <div className={styles.errorMessage}>
            {isNoAccess
              ? intl.formatMessage({
                  id: "resources.filePage.preview.noAccess",
                  defaultMessage:
                    "You do not have permission to view this file. To access, ask the owner to share it with you.",
                  description: "No file access message",
                })
              : intl.formatMessage({
                  id: "resources.filePage.preview.deleted",
                  defaultMessage: "This file has been deleted.",
                  description: "Deleted resource message",
                })}
          </div>
        </div>
      ) : (
        <FilePreviewPage fileId={resource.file.id} name={resource.name} onClose={handleClose} />
      )}
    </EmbeddedModal>
  );
};

const mapDispatchToProps = {
  onFetchResource: fetchResource.request,
};

export default connect(null, mapDispatchToProps)(FilePage);
